import { useCallback, useMemo } from "react";

import classnames from "classnames";
import RDatePicker from "react-datepicker";
import { createPortal } from "react-dom";

import isValidDate from "utils/date/isValidDate/isValidDate";
import { convertLocalToUTCDate, convertUTCToLocalDate } from "utils/utcDateConvertion";

import CustomDateInput from "./CustomDateInput";

const CalendarContainer = ({ children }) => {
  const portalId = "datepicker-portal";
  let portalElement = document.getElementById(portalId);

  if (!portalElement) {
    portalElement = document.createElement("div");
    portalElement.id = portalId;
    portalElement.className = "react-datepicker-popper-portal";
    document.body.appendChild(portalElement);
  }

  return createPortal(children, portalElement);
};

const DatePicker = ({
  disabled,
  name,
  value,
  isInvalid,
  setFieldValue,
  setFieldTouched,
  maxDate,
  minDate,
  handleChange,
  handleFocus,
  handleBlur,
  onCalendarClose,
  popperPlacement = "bottom",
}) => {
  const customHandleChange = useCallback(
    (inputValue) => {
      if (!inputValue) {
        try {
          handleChange(null);
        } catch {
          setFieldValue(name, null);
          setTimeout(() => {
            setFieldTouched(name, true);
          }, 0);
        }
        return;
      }

      const convertedToUtc = convertLocalToUTCDate(inputValue);

      try {
        handleChange(convertedToUtc);
      } catch {
        setFieldValue(name, convertedToUtc);
        setTimeout(() => {
          setFieldTouched(name, true);
        }, 0);
      }
    },
    [setFieldValue, name, setFieldTouched, handleChange],
  );

  const handleCloseCalendar = useCallback(() => {
    if (onCalendarClose && value) {
      onCalendarClose(convertUTCToLocalDate(value));
    }
  }, [onCalendarClose, value]);

  const selectedValue = useMemo(() => {
    if (isValidDate(value)) {
      return convertUTCToLocalDate(value);
    }
    return null;
  }, [value]);

  const maxDateLocal = useMemo(() => {
    if (maxDate && isValidDate(maxDate)) {
      return convertUTCToLocalDate(maxDate);
    }
    return maxDate;
  }, [maxDate]);

  const minDateLocal = useMemo(() => {
    if (minDate && isValidDate(minDate)) {
      return convertUTCToLocalDate(minDate);
    }
    return minDate;
  }, [minDate]);

  return (
    <RDatePicker
      name={name}
      selected={selectedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={customHandleChange}
      onYearChange={customHandleChange}
      onMonthChange={customHandleChange}
      onCalendarClose={handleCloseCalendar}
      maxDate={maxDateLocal}
      minDate={minDateLocal}
      className={classnames("custom-date-picker", { "is-invalid": isInvalid })}
      placeholderText="Select..."
      dateFormat="MMMM d, yyyy"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      disabled={disabled}
      autoComplete="off"
      shouldCloseOnSelect
      customInput={<CustomDateInput name={name} />} // ensure name propagates for selectors
      popperPlacement={popperPlacement}
      popperContainer={CalendarContainer}
    />
  );
};

export default DatePicker;
